import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const TrustedBySection = () => {
  const retailTypes = [
    {
      icon: "ShoppingBag",
      name: "Boutiques",
      count: "1,200+"
    },
    {
      icon: "ShoppingCart",
      name: "Supermarkets",
      count: "850+"
    },
    {
      icon: "Pill",
      name: "Pharmacies",
      count: "650+"
    },
    {
      icon: "Store",
      name: "General Stores",
      count: "2,300+"
    }
  ];

  const stats = [
    {
      value: "5,000+",
      label: "Active Retailers"
    },
    {
      value: "₦2.5B+",
      label: "Transactions Processed"
    },
    {
      value: "98%",
      label: "Customer Satisfaction"
    },
    {
      value: "24/7",
      label: "Nigerian Support"
    }
  ];

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <div className="container mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Trusted by retailers and merchants
          </h2>
          <p className="font-body text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            Growing businesses use Cultaz to run sales, manage inventory, and power online stores - no
            geographic limits.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mb-12 md:mb-14">
          {retailTypes?.map((type, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-card rounded-xl p-6 md:p-8 border border-border shadow-purple hover:shadow-purple-lg transition-smooth text-center"
            >
              <div className="flex h-16 w-16 md:h-20 md:w-20 items-center justify-center rounded-xl bg-primary/10 mx-auto mb-4">
                <Icon name={type?.icon} size={32} color="var(--color-primary)" strokeWidth={2} />
              </div>
              <h3 className="font-heading text-lg md:text-xl font-bold text-foreground mb-2">
                {type?.name}
              </h3>
            </motion.div>
          ))}
        </div>
        {/* Built-with Cultaz showcase */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 md:mt-16"
        >
          <div className="mb-6 text-center">
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Beautiful stores built with Cultaz Cloud</h3>
            <p className="text-muted-foreground mt-2">Real, mobile-friendly e‑commerce stores made by our customers - tap any card to visit.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { name: 'Marivie Int', url: 'https://marivieint.com' },
              { name: 'Dandd African Food', url: 'https://danddafricanfood.com' },
              { name: 'Endress Up', url: 'https://shopendressup.com.ng' },
              { name: 'TA Global Venture', url: 'https://taglobalventure.com' }
            ].map((site) => {
              const host = new URL(site.url).hostname;
              // Use a higher-resolution mshot for Marivie Int (previous 404); fall back to encoded URL for others
              const shot = site.url === 'https://marivieint.com'
                ? `https://s.wordpress.com/mshots/v1/${site.url}?w=1200`
                : `https://s.wordpress.com/mshots/v1/${encodeURIComponent(site.url)}?w=600`;
              return (
                <a
                  key={site.url}
                  href={site.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg transform hover:-translate-y-1 transition-smooth"
                >
                  <div className="w-full h-40 sm:h-36 md:h-32 lg:h-40 bg-muted/10 overflow-hidden">
                    <img
                      src={shot}
                      alt={`${site.name} screenshot`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/assets/images/site-placeholder.svg'; }}
                    />
                  </div>

                  <div className="p-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={`https://s2.googleusercontent.com/s2/favicons?domain=${host}`}
                        alt={`${site.name} favicon`}
                        className="h-8 w-8 rounded-md bg-white p-1"
                        loading="lazy"
                      />
                      <div>
                        <div className="font-semibold text-foreground">{site.name}</div>
                        <div className="text-sm text-muted-foreground">{host}</div>
                      </div>
                    </div>

                    <div className="mt-3">
                      <div className="text-sm text-muted-foreground line-clamp-3">Mobile-ready store with clean UI, payments and checkout - built on Cultaz Cloud.</div>
                    </div>

                    <div className="mt-4 flex items-center justify-between">
                      <span className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full bg-green-100 text-green-800">Mobile Friendly</span>
                      <span className="text-sm text-primary font-semibold">Visit →</span>
                    </div>
                  </div>
                </a>
              );
            })}
          </div>

          {/* "See more stores" removed (page not available) */}
        </motion.div>
      </div>
    </section>
  );
};

export default TrustedBySection;